// Default realistic market-based values for H2 Farm simulator (EUR)
// Aggiornato: Lotto 1 configurazione preferita 300 m² × 6 livelli = 1.800 m²
// equivalenti → 7,2 kg/anno zafferano (4,0 g/m² base scenario).
// Lotto 2 PBR: 36 m³ volume, produttività ~0,194 g/L/giorno × 330 gg → 2.301 kg
// biomassa totale (767 kg × 3 specie).

export const PROD_DAYS_PER_YEAR = 330;

export const DEFAULT_INPUTS = {
  saffron: {
    // Configurazione fisica (INVARIANT — production model, not affected by pricing changes)
    surface_m2: 300,
    levels: 6,
    yield_g_m2: 4.0, // g/m² × livelli (base scenario)
    kg_year: 7.2, // derivato: 300 × 6 × 4 / 1000
    // === Legacy commercial fields (DEPRECATED — kept for backward compat with existing UI/PrintReport) ===
    // The canonical commercial model lives in `saffron.commercial.channels` below.
    // These fields are still consumed by PrintReport.jsx labels and by the existing SaffronTab UI
    // until the UI is migrated (planned block after B.4).
    mix_dried: 60,
    mix_extract: 25,
    mix_scco2: 15,
    yield_extract: 20,
    yield_scco2: 10,
    price_dried: 5000,       // DEPRECATED — see commercial.channels[0].pricing.benchmark_base
    price_extract: 15000,    // DEPRECATED — see commercial.channels[1].pricing.benchmark_base
    price_scco2: 28000,      // DEPRECATED — see commercial.channels[2].pricing.benchmark_base
    // === Canonical commercial model (Phase 2 · B.4) ===
    // Positioning: HIGH-VALUE B2B (Pharma / Nutraceutical / Cosmetics), NOT commodity.
    // Governance-locked benchmark stigma dried: MIN 15k, BASE 25k, MAX 35k, upper premium 40k €/kg.
    // Extracts benchmark: TO_VALIDATE (legacy values preserved as placeholder, status TO_VALIDATE).
    commercial: {
      active_scenario: "base", // "conservative" | "base" | "premium" | "custom"
      positioning: "HIGH_VALUE_B2B",
      channels: [
        {
          key: "dried",
          name: "Stimmi essiccati premium (controlled production)",
          commercial_tier: "PREMIUM_DRIED",
          target_market: "Pharma",
          product_form: "stigma_dried",
          is_intermediate: false,
          allocation_pct: 60,
          classification_allocation: "ASSUMPTION",
          transformation: {
            type: "drying",
            yield_range: { min: 95, base: 100, max: 100, operating: 100, classification: "ASSUMPTION" },
          },
          quality: {
            grade: "GMP",
            active_compound: "crocin",
            standardization: "≥5% crocin via HPLC (target)",
            certifications: [],
            classification: "TO_VALIDATE",
          },
          pricing: {
            benchmark_base: {
              value: 25000, unit: "eur_kg", status: "BENCHMARK",
              market: "Pharma/Nutraceutical B2B EU",
              quality_ref: "GMP-oriented controlled production",
              geographic_market: "EU",
              b2b_or_b2c: "B2B",
              source: "ENEA reference + IDROZAFF pharma-grade concept",
              source_date: "2026-02",
              rationale: "H2 Farm controlled production premium benchmark, NOT commodity food service.",
            },
            range: { min: 15000, base: 25000, max: 35000, unit: "eur_kg", classification: "BENCHMARK" },
            upper_premium_reference: 40000, // €/kg — reference only, not applied unless scenario=custom
            user_override: null,
            contract: null,
          },
        },
        {
          key: "extract_std",
          name: "Estratto standardizzato (crocin)",
          commercial_tier: "STANDARDIZED_EXTRACT",
          target_market: "Nutraceutical",
          product_form: "standardized_extract",
          is_intermediate: false,
          allocation_pct: 25,
          classification_allocation: "ASSUMPTION",
          transformation: {
            type: "solvent_extraction",
            extraction_ratio: "5:1 (target)",
            yield_range: { min: 15, base: 20, max: 25, operating: 20, classification: "ASSUMPTION" },
          },
          quality: {
            grade: "GMP",
            active_compound: "crocin",
            active_compound_pct: null,
            standardization: "TO_VALIDATE",
            certifications: [],
            classification: "TO_VALIDATE",
          },
          pricing: {
            benchmark_base: {
              value: 15000, unit: "eur_kg", status: "TO_VALIDATE",
              market: "Nutraceutical B2B",
              rationale: "Legacy placeholder. Extract price MUST NOT inherit from stigma price — awaiting verified benchmark.",
              source: "legacy simulator value (pre-Phase-2)",
            },
            range: { min: 10000, base: 15000, max: 22000, unit: "eur_kg", classification: "TO_VALIDATE" },
            user_override: null,
            contract: null,
          },
        },
        {
          key: "extract_scco2",
          name: "Estratto SC-CO₂",
          commercial_tier: "HIGH_VALUE_ACTIVE",
          target_market: "Cosmetics",
          product_form: "sc-co2_extract",
          is_intermediate: false,
          allocation_pct: 15,
          classification_allocation: "ASSUMPTION",
          transformation: {
            type: "sc-co2",
            extraction_ratio: "10:1 (target)",
            yield_range: { min: 8, base: 10, max: 12, operating: 10, classification: "ASSUMPTION" },
          },
          quality: {
            grade: "Cosmetic-grade",
            active_compound: "safranal, crocin",
            purity_pct: null,
            standardization: "TO_VALIDATE",
            certifications: [],
            classification: "TO_VALIDATE",
          },
          pricing: {
            benchmark_base: {
              value: 28000, unit: "eur_kg", status: "TO_VALIDATE",
              market: "Cosmetics/Pharma high-value",
              rationale: "Legacy placeholder. High-value active extract benchmark awaiting verified source.",
              source: "legacy simulator value (pre-Phase-2)",
            },
            range: { min: 20000, base: 28000, max: 42000, unit: "eur_kg", classification: "TO_VALIDATE" },
            user_override: null,
            contract: null,
          },
        },
      ],
    },
  },
  algae: {
    // Configurazione fisica condivisa
    pbr_volume_m3: 36,
    productivity_g_l_d: 0.194, // g/L/giorno base (scenario centrale-alto)
    chlorella: {
      name: "Chlorella vulgaris",
      kg_year: 767,
      yield_dried: 92,
      yield_scco2: 6.5,
      price_dried: 40,
      price_scco2: 900,
    },
    spirulina: {
      name: "Spirulina (Arthrospira platensis)",
      kg_year: 767,
      yield_dried: 92,
      yield_scco2: 6.5,
      price_dried: 45,
      price_scco2: 950,
    },
    scenedesmus: {
      name: "Scenedesmus obliquus",
      kg_year: 767,
      yield_dried: 92,
      yield_scco2: 6.5,
      price_dried: 38,
      price_scco2: 850,
    },
  },
  opex: {
    energy: 4500,
    consumables: 3200,
    labor: 12500,
    maintenance: 2800,
  },
  capex: {
    initial_investment: 420000, // baseline: ~800€/m² saffron + ~5k€/m³ PBR
    discount_rate: 8, // % (per calcoli NPV/IRR di riferimento)
  },
  projection: {
    reinvestment_pct: 40,
    expansion_capex_per_pct: 25000,
    opex_inflation_pct: 3,
    revenue_inflation_pct: 2,
  },
  future_lots: [
    {
      id: "lot3",
      name: "Lotto 3 — Funghi Premium",
      description: "Ganoderma, Cordyceps, Hericium (indoor 250 m²)",
      surface_m2: 250,
      annual_revenue: 180000,
      annual_opex: 96000,
      capex: 140000,
      enabled: true,
    },
    {
      id: "lot4",
      name: "Lotto 4 — Frutti Rossi",
      description: "Fragole & lamponi in fuori suolo (indoor 250 m²)",
      surface_m2: 250,
      annual_revenue: 120000,
      annual_opex: 78000,
      capex: 110000,
      enabled: true,
    },
    {
      id: "lot5",
      name: "Lotto 5 — Da Definire",
      description: "Slot riservato per futura verticale produttiva.",
      surface_m2: 200,
      annual_revenue: 0,
      annual_opex: 0,
      capex: 0,
      enabled: false,
    },
  ],
  new_lots: {
    // LOTTO 3 — Bioactive Botanicals
    lot3: {
      id: "lot3",
      name: "Lotto 3 — Bioactive Botanicals",
      description: "Frutti rossi come materia prima B2B nutraceutica/cosmetica.",
      physical_area_m2: 300,
      vertical_levels: 1,
      equivalent_area_m2: 300,
      classification_area: "USER_INPUT",
      activation_year: 2,
      enabled: true,
      species: [
        { key: "aronia", name: "Aronia melanocarpa", allocation_pct: 25, actives: "antociani · proantocianidine · polifenoli" },
        { key: "ribes", name: "Ribes nigrum", allocation_pct: 25, actives: "antociani · flavonoidi · polifenoli" },
        { key: "mirtillo", name: "Vaccinium myrtillus", allocation_pct: 15, actives: "antociani · flavonoidi · polifenoli" },
        { key: "olivello", name: "Hippophae rhamnoides", allocation_pct: 20, actives: "polifenoli · flavonoidi · carotenoidi · frazioni lipidiche" },
        { key: "mora", name: "Rubus fruticosus", allocation_pct: 10, actives: "antociani · ellagitannini · polifenoli" },
        { key: "lampone", name: "Rubus idaeus", allocation_pct: 5, actives: "ellagitannini · antociani · polifenoli" },
      ],
      classification_species: "TO_VALIDATE",
      scenario: "base",
      production_scenarios: { conservative: 3500, base: 4000, optimistic: 4500 },
      classification_production: "ASSUMPTION",
      hard_bounds_kg: { min: 3500, max: 4500, warn_over: 5000, critical_over: 6000 },
      commercialization_path: [
        { key: "fresco", name: "Fresco", pct: 0 },
        { key: "essiccato", name: "Essiccato", pct: 30 },
        { key: "polvere", name: "Polvere", pct: 20 },
        { key: "estratto", name: "Estratto grezzo", pct: 30 },
        { key: "std", name: "Estratto standardizzato", pct: 20 },
      ],
      classification_path: "ASSUMPTION",
      revenue_scenarios: { conservative: 80000, base: 110000, optimistic: 150000 },
      classification_revenue: "ASSUMPTION",
      opex_scenarios: { conservative: 55000, base: 68000, optimistic: 85000 },
      classification_opex: "ASSUMPTION",
      capex_scenarios: { min: 90000, base: 135000, max: 180000 },
      classification_capex: "ASSUMPTION",
      energy_kwh_year: 55000,
      water_m3_year: 850,
      classification_utilities: "ASSUMPTION",
    },
    // LOTTO 4 — Fungi High Value
    lot4: {
      id: "lot4",
      name: "Lotto 4 — Fungi High Value",
      description: "Funghi funzionali su rack verticali (Lion's Mane, Maitake, Shiitake…).",
      physical_area_m2: 300,
      vertical_levels: 4,
      equivalent_area_m2: 1200,
      classification_area: "USER_INPUT",
      activation_year: 3,
      enabled: true,
      species: [
        { key: "hericium", name: "Hericium erinaceus (Lion's Mane)", allocation_pct: 25, actives: "beta-glucani · ericenoni · erinacine" },
        { key: "maitake", name: "Grifola frondosa (Maitake)", allocation_pct: 15, actives: "beta-glucani · polisaccaridi D-fraction" },
        { key: "shiitake", name: "Lentinula edodes (Shiitake)", allocation_pct: 15, actives: "beta-glucani · lentinano · ergosterolo" },
        { key: "eryngii", name: "Pleurotus eryngii (King Oyster)", allocation_pct: 15, actives: "beta-glucani · ergosterolo" },
        { key: "ostreatus", name: "Pleurotus ostreatus funzionale", allocation_pct: 15, actives: "beta-glucani · polisaccaridi" },
        { key: "enoki", name: "Flammulina velutipes (Enoki)", allocation_pct: 15, actives: "beta-glucani · flammulina proteine" },
      ],
      classification_species: "TO_VALIDATE",
      scenario: "base",
      production_scenarios: { conservative: 8400, base: 9600, optimistic: 10800 },
      classification_production: "ASSUMPTION",
      hard_bounds_kg: { min: 8400, max: 10800, warn_over: 13000, critical_over: 20000 },
      commercialization_path: [
        { key: "fresco", name: "Fresco", pct: 0 },
        { key: "essiccato", name: "Essiccato", pct: 30 },
        { key: "polvere", name: "Polvere", pct: 25 },
        { key: "estratto", name: "Estratto", pct: 25 },
        { key: "std", name: "Estratto standardizzato", pct: 20 },
      ],
      classification_path: "ASSUMPTION",
      revenue_scenarios: { conservative: 120000, base: 170000, optimistic: 220000 },
      classification_revenue: "ASSUMPTION",
      opex_scenarios: { conservative: 78000, base: 96000, optimistic: 118000 },
      classification_opex: "ASSUMPTION",
      capex_scenarios: { min: 110000, base: 165000, max: 220000 },
      classification_capex: "ASSUMPTION",
      energy_kwh_year: 95000,
      water_m3_year: 620,
      classification_utilities: "ASSUMPTION",
    },
    // LOTTO 5 — Luppolo Verticale
    lot5: {
      id: "lot5",
      name: "Lotto 5 — Luppolo Verticale",
      description: "Humulus lupulus in coltivazione verticale per estratti bioattivi.",
      physical_area_m2: 300,
      vertical_levels: 2,
      equivalent_area_m2: 600,
      plants_per_m2_per_level: 4,
      total_plants: 2400, // 300 × 2 × 4
      classification_area: "USER_INPUT",
      activation_year: 4,
      enabled: true,
      species: [
        { key: "cascade", name: "Humulus lupulus cv. Cascade", allocation_pct: 35, actives: "alfa-acidi · beta-acidi · xantumolo" },
        { key: "chinook", name: "Humulus lupulus cv. Chinook", allocation_pct: 30, actives: "alfa-acidi · myrcene · polifenoli" },
        { key: "centennial", name: "Humulus lupulus cv. Centennial", allocation_pct: 20, actives: "xantumolo · flavonoidi" },
        { key: "italiano", name: "Cultivar italiano da definire", allocation_pct: 15, actives: "TO_VALIDATE" },
      ],
      classification_species: "TO_VALIDATE",
      scenario: "base",
      g_per_plant_scenarios: { conservative: 250, base: 400, optimistic: 550 },
      production_scenarios: { conservative: 600, base: 960, optimistic: 1320 },
      classification_production: "ASSUMPTION",
      hard_bounds_kg: { min: 600, max: 1320, warn_over: 1600, critical_over: 2500 },
      commercialization_path: [
        { key: "coni", name: "Coni secchi", pct: 30 },
        { key: "pellet", name: "Pellet", pct: 20 },
        { key: "estratto", name: "Estratto grezzo", pct: 20 },
        { key: "std", name: "Estratto standardizzato", pct: 20 },
        { key: "bioactive", name: "Frazioni bioattive (xantumolo, alfa-acidi)", pct: 10 },
      ],
      classification_path: "ASSUMPTION",
      revenue_scenarios: { conservative: 20000, base: 40000, optimistic: 60000 },
      classification_revenue: "ASSUMPTION",
      opex_scenarios: { conservative: 18000, base: 26000, optimistic: 34000 },
      classification_opex: "ASSUMPTION",
      capex_scenarios: { min: 80000, base: 110000, max: 145000 },
      classification_capex: "ASSUMPTION",
      energy_kwh_year: 42000,
      water_m3_year: 780,
      classification_utilities: "ASSUMPTION",
    },
  },
  model_version: {
    version: "2.4.0",
    updated_at: "2026-02-15",
    changes: [
      { date: "2026-02-15", param: "new_lots (Lotti 3-4-5)", old: "n/a", new: "added Bioactive Botanicals + Fungi + Hops", reason: "Piano espansione H2 Farm 2026-2030", classification: "USER_INPUT" },
    ],
  },
  energy: {
    // Old estimate (kept as reference for change summary):
    previous_kwp: 1132,
    previous_gwh: 1.867,
    // Monthly production shares (Grosseto/Maremma, sum = 1.000)
    monthly_profile: [0.040, 0.055, 0.080, 0.100, 0.120, 0.130, 0.135, 0.120, 0.095, 0.065, 0.035, 0.025],
    // 4 PV subsystems
    subsystems: {
      bacino: {
        name: "A · FV Galleggiante Bacino",
        enabled: true,
        surface_m2: 250,
        coverage_pct: 85,
        wp_per_m2: 200,
        specific_prod: 1650,
        performance_ratio: 100,
        capex_eur_kwp: 1300,
        classification: "ASSUMPTION",
      },
      serra: {
        name: "B · FV Semitrasparente Serra",
        enabled: true,
        surface_m2: 300,
        wp_per_m2: 100, // agrivoltaico: minore per lasciare luce colture
        transparency_pct: 40, // % luce trasmessa (non entra nel calcolo produzione)
        specific_prod: 1650,
        performance_ratio: 100,
        capex_eur_kwp: 1500,
        classification: "TO_VALIDATE",
      },
      terra: {
        name: "C · FV a Terra Bifacciale",
        enabled: true,
        terrain_m2: 5000,
        occupation_pct: 75, // GCR reale
        wp_per_m2: 200,
        bifacial: true,
        bifacial_gain_pct: 0, // gain aggiuntivo bifaccialità
        tilt: 30,
        orientation: "Sud",
        specific_prod: 1650,
        performance_ratio: 100,
        capex_eur_kwp: 900,
        classification: "TO_VALIDATE",
      },
      tettoie: {
        name: "D · FV Tettoie · Parcheggi · Area H₂",
        enabled: true,
        surface_m2: 150,
        wp_per_m2: 200,
        specific_prod: 1650,
        performance_ratio: 100,
        capex_eur_kwp: 1000,
        classification: "ASSUMPTION",
      },
    },
    // 11 categorie di consumo — MVP (senza lotti futuri, sono aggiunti dinamicamente)
    load_categories: [
      { id: "l1", name: "Lotto 1 Zafferano (LED + climatizzazione)", kwh_year: 95000, classification: "ASSUMPTION" },
      { id: "l2", name: "Lotto 2 Microalghe (LED PBR + pompe)", kwh_year: 110000, classification: "ASSUMPTION" },
      { id: "process", name: "Processazione (packaging, taglio)", kwh_year: 15000, classification: "ASSUMPTION" },
      { id: "refrig", name: "Refrigerazione (cella frigo)", kwh_year: 18000, classification: "ASSUMPTION" },
      { id: "irrig", name: "Irrigazione · Pompe · Trattamento acqua", kwh_year: 15000, classification: "ASSUMPTION" },
      { id: "lab", name: "Laboratorio · IT · Automazione", kwh_year: 12000, classification: "ASSUMPTION" },
      { id: "hvac", name: "HVAC · Deumidificatori · Ventole", kwh_year: 90000, classification: "ASSUMPTION" },
      { id: "drying", name: "Essiccazione (forni sottovuoto)", kwh_year: 35000, classification: "ASSUMPTION" },
      { id: "extract", name: "Estrazione SC-CO₂", kwh_year: 55000, classification: "ASSUMPTION" },
      { id: "electro", name: "Elettrolizzatore (autoconsumo aux)", kwh_year: 6000, classification: "ASSUMPTION" },
      { id: "aux", name: "Altri ausiliari · Illuminazione uffici", kwh_year: 9000, classification: "ASSUMPTION" },
    ],
    // BESS (batteria)
    battery: {
      capacity_kwh: 500,
      round_trip_efficiency_pct: 92,
      dod_pct: 90,
      capex_eur_kwh: 500,
      classification: "ASSUMPTION",
    },
    // Elettrolizzatore
    electrolyzer: {
      power_kw: 200,
      kwh_per_kg_h2: 55, // PEM realistico
      operating_hours_year: 3500,
      surplus_share_pct: 100,
      storage_pressure_bar: 350,
      storage_capacity_kg: 500,
      capex_eur_kw: 1500,
      storage_capex_eur_kg: 800,
      classification: "ASSUMPTION",
    },
    // Fuel cell (usata prevalentemente in inverno)
    fuel_cell: {
      power_kw: 100,
      electric_efficiency_pct: 50,
      heat_recovery_pct: 40,
      useful_heat_temp_c: 65,
      operating_hours_year: 2500,
      capex_eur_kw: 3000,
      classification: "ASSUMPTION",
    },
    // Economics
    grid_price_eur_kwh: 0.28,
    grid_feed_price_eur_kwh: 0.09,
    h2_price_eur_kg: 8,
    // Integrazione con simulatore
    include_in_capex: true,
    apply_opex_savings: true,
    install_year: 1, // 1 = anno 1 (subito), configurabile
    active_scenario: "base",
    classifications: {
      monthly_profile: "ASSUMPTION",
      specific_prod: "ASSUMPTION",
      consumption_mvp: "ASSUMPTION",
    },
  },
};

// --- ENERGY SCENARIOS (indipendenti dai lotti produttivi) --
// Applicati con overlay solo sui sottosistemi FV
export const ENERGY_SCENARIOS = {
  conservative: {
    name: "Conservative (~700 kWp · 1,15 GWh)",
    target_kwp: 700,
    subsystems: {
      bacino: { surface_m2: 250, coverage_pct: 85, wp_per_m2: 200 }, // 42.5 kWp
      serra: { surface_m2: 300, wp_per_m2: 60 }, // 18 kWp
      terra: { terrain_m2: 5000, occupation_pct: 62, wp_per_m2: 200 }, // 620 kWp
      tettoie: { surface_m2: 100, wp_per_m2: 200 }, // 20 kWp
    },
  },
  base: {
    name: "Base (~850 kWp · 1,40 GWh)",
    target_kwp: 850,
    subsystems: {
      bacino: { surface_m2: 250, coverage_pct: 85, wp_per_m2: 200 }, // 42.5 kWp
      serra: { surface_m2: 300, wp_per_m2: 100 }, // 30 kWp
      terra: { terrain_m2: 5000, occupation_pct: 75, wp_per_m2: 200 }, // 750 kWp
      tettoie: { surface_m2: 150, wp_per_m2: 200 }, // 30 kWp
    },
  },
  optimistic: {
    name: "Optimistic (~1050 kWp · 1,73 GWh)",
    target_kwp: 1050,
    subsystems: {
      bacino: { surface_m2: 300, coverage_pct: 90, wp_per_m2: 220 }, // 59.4 kWp
      serra: { surface_m2: 400, wp_per_m2: 130 }, // 52 kWp
      terra: { terrain_m2: 5500, occupation_pct: 82, wp_per_m2: 210 }, // 947 kWp
      tettoie: { surface_m2: 180, wp_per_m2: 210 }, // 37.8 kWp
    },
  },
};

// Costi CAPEX unitari per stima varianti nell'Optimizer
export const CAPEX_UNIT = {
  saffron_per_m2: 800, // rack aeroponici + illuminazione + climatizzazione
  algae_per_m3: 5000, // fotobioreattori tubulari + circuiti + harvesting
};

// Opzioni Optimizer
export const OPTIMIZER_SURFACES = [250, 300, 350]; // m² Lotto 1
export const OPTIMIZER_VOLUMES = [36, 45, 50, 60]; // m³ Lotto 2

export const PRESET_SCENARIOS = {
  baseline: {
    name: "Scenario Base (300 m² · 36 m³)",
    description:
      "Config preferita: 300 m² zafferano (1.800 m² eq., 7,2 kg) + 36 m³ PBR (2,3 t microalghe). Include lotti 3-4.",
    inputs: DEFAULT_INPUTS,
  },
  snapshot_start: {
    name: "Snapshot Iniziale (-€31.663)",
    description:
      "Configurazione originale 250 m² · 5,5 kg zafferano, no lotti futuri. Riferimento -€31.663 EBITDA.",
    inputs: {
      ...DEFAULT_INPUTS,
      saffron: {
        ...DEFAULT_INPUTS.saffron,
        surface_m2: 250,
        yield_g_m2: 3.667,
        kg_year: 5.5,
      },
      future_lots: DEFAULT_INPUTS.future_lots.map((l) => ({ ...l, enabled: false })),
    },
  },
  optimistic: {
    name: "Scenario Ottimistico",
    description:
      "350 m² zafferano (8,4 kg) + 60 m³ PBR + rese massime + prezzi premium + tutti i lotti futuri attivi.",
    inputs: {
      ...DEFAULT_INPUTS,
      saffron: {
        ...DEFAULT_INPUTS.saffron,
        surface_m2: 350,
        yield_g_m2: 4.0,
        kg_year: 8.4,
        mix_dried: 30,
        mix_extract: 35,
        mix_scco2: 35,
        yield_extract: 25,
        yield_scco2: 12,
        price_dried: 7500,
        price_extract: 22000,
        price_scco2: 42000,
      },
      algae: {
        pbr_volume_m3: 60,
        productivity_g_l_d: 0.2,
        chlorella: { name: "Chlorella vulgaris", kg_year: 1320, yield_scco2: 15, yield_dried: 82, price_dried: 65, price_scco2: 1400 },
        spirulina: { name: "Spirulina (Arthrospira platensis)", kg_year: 1320, yield_scco2: 15, yield_dried: 82, price_dried: 70, price_scco2: 1500 },
        scenedesmus: { name: "Scenedesmus obliquus", kg_year: 1320, yield_scco2: 15, yield_dried: 82, price_dried: 60, price_scco2: 1300 },
      },
      capex: { initial_investment: 720000, discount_rate: 8 },
      projection: { ...DEFAULT_INPUTS.projection, reinvestment_pct: 60 },
      future_lots: DEFAULT_INPUTS.future_lots.map((l) => ({ ...l, enabled: true, annual_revenue: l.annual_revenue * 1.2 })),
    },
  },
  conservative: {
    name: "Scenario Conservativo",
    description:
      "250 m² · 4,8 kg zafferano + 36 m³ PBR con rese minime, OPEX elevata, solo lotti 1-2.",
    inputs: {
      ...DEFAULT_INPUTS,
      saffron: {
        ...DEFAULT_INPUTS.saffron,
        surface_m2: 250,
        yield_g_m2: 3.2,
        kg_year: 4.8,
        mix_dried: 85,
        mix_extract: 10,
        mix_scco2: 5,
        yield_extract: 15,
        yield_scco2: 8,
        price_dried: 3250,
        price_extract: 10000,
        price_scco2: 20000,
      },
      algae: {
        pbr_volume_m3: 36,
        productivity_g_l_d: 0.1,
        chlorella: { name: "Chlorella vulgaris", kg_year: 396, yield_scco2: 5, yield_dried: 92, price_dried: 28, price_scco2: 650 },
        spirulina: { name: "Spirulina (Arthrospira platensis)", kg_year: 396, yield_scco2: 5, yield_dried: 92, price_dried: 32, price_scco2: 700 },
        scenedesmus: { name: "Scenedesmus obliquus", kg_year: 396, yield_scco2: 5, yield_dried: 92, price_dried: 25, price_scco2: 600 },
      },
      opex: { energy: 5800, consumables: 4200, labor: 14500, maintenance: 3800 },
      capex: { initial_investment: 380000, discount_rate: 10 },
      projection: { ...DEFAULT_INPUTS.projection, reinvestment_pct: 20 },
      future_lots: DEFAULT_INPUTS.future_lots.map((l) => ({ ...l, enabled: false })),
    },
  },
};
